import React, { useEffect, useState } from "react";
import { useSelector } from 'react-redux';
import { useTranslation, Trans } from "react-i18next";
import { useLocation } from 'react-router-dom'

import {
  Input,
  Button,
  Divider,
  Card
} from '@lockerpm/design';

import paymentServices from "../../../../../services/payment";

import global from "../../../../../config/global";
import common from "../../../../../utils/common";

const PayAndUpgrade = (props) => {
  const { t } = useTranslation();
  const location = useLocation();

  const {
    className = '',
    planTypes = {},
    planPeriod = {},
    selectedPlan = {},
    selectedPeriod = {},
    result = {},
    setResult = () => {},
    selectedCard = {},
    discountPercentage = 0,
    nextBill = '',
    familyMembers = [],
    onDone = () => {}
  } = props;

  const plan = useSelector((state) => state.payment.plan);
  const currentPage = common.getRouterByLocation(location)

  const [callingAPI, setCallingAPI] = useState(false)
  const [promoCode, setPromoCode] = useState(currentPage.query.code)
  const [loading, setLoading] = useState(false)
  const [calling, setCalling] = useState(false)

  useEffect(() => {
    calcPrice(true);
  }, [selectedPlan, selectedPeriod])

  const calcPrice = async (isLoading = false) => {
    if (!selectedPlan.alias || !selectedPeriod.duration) {
      setResult({});
      return
    }
    const currency = 'USD'
    const numberMembers = selectedPlan.alias === planTypes.FAMILY ? 6 : 1
    setLoading(isLoading);
    setCalling(true);
    await paymentServices.calc({
      plan_alias: selectedPlan.alias,
      promo_code: promoCode,
      duration: selectedPeriod.duration,
      number_members: numberMembers,
      currency
    }).then((res) => {
      setResult(res);
      if (res.error_promo) {
        setPromoCode('')
        global.pushError({
          message: t('notification.error.payment.invalid_code')
        });
      }
    }).catch((error) => {
      setPromoCode('')
      global.pushError(error)
    })
    setCalling(false);
    setLoading(false);
  }

  const confirmPlan = async () => {
    let pCode = promoCode;
    if (promoCode && result.error_promo) {
      pCode = '';
    }
    setCallingAPI(true);
    const shareKey = await global.jsCore.cryptoService.makeShareKey();
    const orgKey = shareKey[0].encryptedString;
    const collection = await global.jsCore.cryptoService.encrypt(
      'defaultCollection',
      shareKey[1]
    );
    const collectionName = collection.encryptedString;
    const members = selectedPlan.alias === planTypes.FAMILY ? familyMembers : [];
    const numberMembers = selectedPlan.alias === planTypes.FAMILY ? 6 : 1;

    const pmClickCS = common.getPmClickCS();
    const pmClick = common.getPmClick();

    await paymentServices.create_plan({
      plan_alias: selectedPlan.alias,
      duration: selectedPeriod.duration,
      payment_method: 'card',
      id_card: selectedCard.id_card,
      promo_code: pCode,
      number_members: numberMembers,
      key: orgKey,
      collection_name: collectionName,
      family_members: members,
      click_uuid: pmClickCS?.click || pmClick?.click || ''
    }).then(() => {
      onDone();
      setTimeout(() => {
        common.getPaymentPlan();
      }, 10000);
      if (!["dev", "staging"].includes(global.constants.ENVIRONMENT)) {
        // GA Tracking
        common.trackGAPurchase({
          planId: `${selectedPlan.alias}_${selectedPeriod.duration}`,
          value: result.price,
          currency: result.currency,
          discount: result.discount,
          coupon: pCode,
        })
      }
    }).catch((error) => {
      global.pushError(error);
    })

    setCallingAPI(false);
  }

  return (
    <div className={className}>
      <Card
        loading={loading}
        title={
          <p className="text-[18px] font-semibold">
            {t('manage_plans.payment_step.step_3')}
          </p>
        }
      >
        <div className="flex justify-between">
          <div className="pr-3">
            <p className="text-black-600">
              Locker {selectedPlan.name}
              <br />
              {t(selectedPeriod.label)}
            </p>
          </div>
          <p className="text-xl text-black-800">
            {common.formatCurrency(result.price, 2, result.currency)}
          </p>
        </div>
        {/* Promo code */}
        <div className="mt-3">
          {
            !!(result.discount && promoCode) && <div className="flex justify-between">
              <p className="text-primary italic pr-2">
                {promoCode}
              </p>
              <div className="text-right">
                <p className="text-black-600">
                  -{common.formatCurrency(result.discount, 2, result.currency)}
                </p>
                <p className="text-warning">{discountPercentage}% off</p>
              </div>
            </div>
          }
          {
            !(result.discount && promoCode) && <div className="flex items-center">
              <Input
                value={promoCode}
                placeholder={t('manage_plans.payment_step.enter_code')}
                className="mr-2"
                disabled={calling}
                onChange={(e) => setPromoCode(e.target.value)}
              />
              <Button
                type="primary"
                disabled={!promoCode}
                loading={calling}
                onClick={() => calcPrice()}
              >
                {t('button.add')}
              </Button>
            </div>
          }
        </div>
        {/* Promo code end*/}
        <Divider />
        <div className="flex justify-between">
          <p className="font-semibold text-[16px] pr-3">
            {t('common.total')}
          </p>
          <p className="font-semibold text-[16px]">
            {
              !plan?.personal_trial_applied && <span className="text-black-600 line-through mr-2">
                {common.formatCurrency(result.next_billing_payment ? result.next_billing_payment : result.price, 2, result.currency)}
              </span>
            }
            <span>
              {common.formatCurrency(result.immediate_payment, 2, result.currency)}
            </span>
            {
              !plan?.personal_trial_applied && <span>*</span>
            }
          </p>
        </div>
        <div className="text-black-600 mb-4 mt-2">
          {
            !plan?.personal_trial_applied && <div>
              <p>
                <Trans
                  i18nKey={'billing.trial_summary'}
                  components={{
                    b: <b className="font-semibold"/>
                  }}
                  values={{
                    duration: result.duration || planPeriod.YEARLY,
                    price:  result.next_billing_payment ? result.next_billing_payment : result.price || 0,
                    currency: result.currency || 'USD',
                    next_bill: nextBill || ''
                  }}
                />
              </p>
              <p className="mt-2">{t('billing.trial_summary1')}</p>
            </div>
          }
          {
            !!plan?.personal_trial_applied && <div>
              <p>
                <Trans
                  i18nKey={'billing.plan_details'}
                  components={{
                    b: <b className="font-semibold"/>
                  }}
                  values={{
                    duration: result.duration || planPeriod.YEARLY,
                    price:  result.next_billing_payment ? result.next_billing_payment : result.price || 0,
                    currency: result.currency || 'USD',
                    next_bill: nextBill || ''
                  }}
                />
              </p>
              <p className="mt-2">{t('billing.plan_details1')}</p>
            </div>
          }
        </div>
        <Button
          type="primary"
          className="w-full"
          disabled={!selectedCard || !selectedCard.id_card}
          loading={callingAPI}
          onClick={confirmPlan}
        >
          {t('billing.pay_and_upgrade')}
        </Button>
      </Card>
    </div>
  );
}

export default PayAndUpgrade;